import React from "react";
import { css } from "@emotion/css";
import LinearProgress from "@mui/material/LinearProgress";
import * as PropTypes from "prop-types";
import { allRegionsId } from "../../constants";
import { databaseTypePropType, periodPropType } from "../../propTypes";
import SelectSurveyDatabase from "./drawer/SelectSurveyDatabase";
import SelectPopulationDatabase from "./drawer/SelectPopulationDatabase";
import SelectServiceStatistics from "./drawer/SelectServiceStatistics";
import SelectCountry from "./drawer/SelectCountry";
import SelectNationalSubnational from "./drawer/SelectNationalSubnational";
import InputRunSettings from "./drawer/InputRunSettings";
import ButtonReviewData from "./drawer/ButtonReviewData";
import ButtonStartRun from "./drawer/ButtonStartRun";
// import SelectCountryPopulation from "./drawer/SelectCountryPopulation";
// import SelectPopulation from "./drawer/SelectPopulation";

const classes = {
    actionButton: css`
        display: inline-flex;
        min-height: 36px;
        margin-top: 10px;
    `,
    divider: css`
        margin: 10px 0;
    `,
    drawerItem: css`
        flex-shrink: 0;
        z-index: 0;
    `,
    instruction: css`
        margin: 10px 0;
        font-weight: bold;
    `,
    periodSelector: css`
        margin: 40px 10px;
    `,
    periodSelectorGroup: css`
        margin-top: 10px;
        z-index: -1;
    `,
    periodSelectorLabel: css`
        font-size: 75%;
    `,
    progress: css`
        margin: 15px 0 5px;
    `,
    select: css`
        margin-bottom: 10px;
    `,
};

const groupDatabases = (databases) => {
    const denormalizedDatabases = Object.entries(databases).map(([id, database]) => ({
        id: id,
        ...database,
    }));

    /* prettier-ignore */
    return {
    surveyDatabases: denormalizedDatabases.filter((database) => database.type === "survey" && database.isGlobal),
    customSurveyDatabases: denormalizedDatabases.filter((database) => database.type === "survey" && !database.isGlobal),
    populationDatabases: denormalizedDatabases.filter((database) => database.type === "population" && database.isGlobal),
    customPopulationDatabases: denormalizedDatabases.filter((database) => database.type === "population" && !database.isGlobal),
    emuDatabases: denormalizedDatabases.filter((database) => database.type === "emu" && database.isGlobal),
    customEmuDatabases: denormalizedDatabases.filter((database) => database.type === "emu" && !database.isGlobal),
  };
};

const getCountries = (countries) => {
    const result = Object.entries(countries || {})
        .map(([numericCode, country]) => ({
            value: parseInt(numericCode, 10),
            label: country.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    return result;
};

const getRegions = (regions) => {
    const result = regions.map((region, index) => ({
        value: -index - 1,
        label: region,
    }));
    return result;
};

const StartRunDrawerContent = (props) => {
    const {
        surveyDatabases,
        customSurveyDatabases,
        populationDatabases,
        customPopulationDatabases,
        customEmuDatabases,
    } = groupDatabases(props.databases);

    const countries = getCountries(props.countries);
    const regions = getRegions(props.regions);

    const selectedRegion = regions.find((region) => region.label === props.selectedRegion);
    const selectedRegionValue = selectedRegion && selectedRegion.value;

    let countriesAndRegions = countries.concat(regions);

    if (countriesAndRegions.length > 1) {
        countriesAndRegions = [
            {
                value: allRegionsId,
                label: "All regions",
            },
            ...countriesAndRegions,
        ];
    }

    if (props.loadingData) {
        return (
            <div className={classes.progress}>
                <LinearProgress />
            </div>
        );
    }

    return (
        <>
            {/* {props.loadingData && (
                <div className={classes.progress}>
                <LinearProgress />
                </div>
            )} */}

            <SelectSurveyDatabase
                {...props}
                defaultDatabases={surveyDatabases}
                customDatabases={customSurveyDatabases}
            />

            <SelectPopulationDatabase
                {...props}
                defaultDatabases={populationDatabases}
                customDatabases={customPopulationDatabases}
            />

            <SelectServiceStatistics
                {...props}
                customEmuDatabases={customEmuDatabases}
            />

            <SelectCountry
                {...props}
                countries={countries}
            />

            <SelectNationalSubnational
                {...props}
                regions={regions}
                selectedRegion={selectedRegion}
                selectedRegionValue={selectedRegionValue}
            />

            <InputRunSettings {...props} />

            <ButtonReviewData {...props} />

            <ButtonStartRun {...props} />
        </>
    );
};

StartRunDrawerContent.propTypes = {
    loadingData: PropTypes.bool,
    databases: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ),
    surveyDatabaseType: databaseTypePropType,
    selectedSurveyDatabaseId: PropTypes.string,
    uploadedSurveyDatabaseName: PropTypes.string,
    uploadingSurveyDatabase: PropTypes.bool,
    surveyDatabaseUploadProgress: PropTypes.number,
    populationDatabaseType: databaseTypePropType,
    selectedPopulationDatabaseId: PropTypes.string,
    uploadedPopulationDatabaseName: PropTypes.string,
    uploadingPopulationDatabase: PropTypes.bool,
    populationDatabaseUploadProgress: PropTypes.number,
    emuDatabaseType: databaseTypePropType,
    selectedEmuDatabaseId: PropTypes.string,
    uploadedEmuDatabaseName: PropTypes.string,
    uploadingEmuDatabase: PropTypes.bool,
    emuDatabaseUploadProgress: PropTypes.number,
    countries: PropTypes.objectOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
        })
    ),
    selectedCountryNumericCode: PropTypes.number,
    regions: PropTypes.arrayOf(PropTypes.string),
    selectedRegion: PropTypes.string,
    runName: PropTypes.string,
    period: periodPropType,
    loadingTables: PropTypes.bool,
    onChangeSurveyDatabaseType: PropTypes.func.isRequired,
    onSelectSurveyDatabase: PropTypes.func.isRequired,
    onSelectSurveyDatabaseFile: PropTypes.func.isRequired,
    onUploadSurveyDatabase: PropTypes.func.isRequired,
    onCancelSurveyDatabaseUpload: PropTypes.func.isRequired,
    onChangePopulationDatabaseType: PropTypes.func.isRequired,
    onSelectPopulationDatabase: PropTypes.func.isRequired,
    onSelectPopulationDatabaseFile: PropTypes.func.isRequired,
    onUploadPopulationDatabase: PropTypes.func.isRequired,
    onCancelPopulationDatabaseUpload: PropTypes.func.isRequired,
    onChangeEmuDatabaseType: PropTypes.func.isRequired,
    onSelectEmuDatabase: PropTypes.func.isRequired,
    onSelectEmuDatabaseFile: PropTypes.func.isRequired,
    onUploadEmuDatabase: PropTypes.func.isRequired,
    onCancelEmuDatabaseUpload: PropTypes.func.isRequired,
    onSelectCountry: PropTypes.func.isRequired,
    onSelectRegion: PropTypes.func.isRequired,
    onRunNameChanged: PropTypes.func.isRequired,
    onChangePeriod: PropTypes.func.isRequired,
    onReviewData: PropTypes.func.isRequired,
    onStartRun: PropTypes.func.isRequired,
};

StartRunDrawerContent.defaultProps = {
    loadingData: false,
    databases: {},
    surveyDatabaseType: "default",
    uploadingSurveyDatabase: false,
    populationDatabaseType: "default",
    uploadingPopulationDatabase: false,
    emuDatabaseType: "default",
    uploadingEmuDatabase: false,
    regions: [],
    loadingTables: false,
};

export default StartRunDrawerContent;
